import type { AvailableControls, ControlsInput } from '@shared/rich-text-editor-core';
import React from 'react';

import {
  FormatAlignCenterOutlinedIcon,
  FormatAlignLeftOutlinedIcon,
  FormatAlignRightOutlinedIcon,
  FormatBoldOutlinedIcon,
  FormatItalicOutlinedIcon,
  FormatListBulletedOutlinedIcon,
  FormatListNumberedOutlinedIcon,
  FormatUnderlinedOutlinedIcon,
  SubscriptOutlinedIcon,
  SuperscriptOutlinedIcon,
} from '../../icons';
import ImageControl from '../controls/ImageControl';
import { BetaControl, SdControl, SummationControl } from '../controls/InsertLatexShortcutControl';
import LatexControl from '../controls/LatexControl';
import { makeSimpleControl } from '../controls/makeSimpleControl';
import TableControl from '../controls/TableControl';

export interface ToolbarProps {
  controls: ControlsInput;
}

type ControlComponent = React.FC;
type ControlComponentMap = Partial<Record<AvailableControls, ControlComponent>>;

const CONTROL_COMPONENTS: ControlComponentMap = {
  bold: makeSimpleControl('Bold', FormatBoldOutlinedIcon, { name: 'toggleBold' }, { name: 'bold' }),
  italic: makeSimpleControl(
    'Italic',
    FormatItalicOutlinedIcon,
    { name: 'toggleItalic' },
    { name: 'italic' },
  ),
  underline: makeSimpleControl(
    'Underline',
    FormatUnderlinedOutlinedIcon,
    { name: 'toggleUnderline' },
    { name: 'underline' },
  ),
  subscript: makeSimpleControl(
    'Subscript',
    SubscriptOutlinedIcon,
    { name: 'toggleSubscript' },
    { name: 'subscript' },
  ),
  superscript: makeSimpleControl(
    'Superscript',
    SuperscriptOutlinedIcon,
    { name: 'toggleSuperscript' },
    { name: 'superscript' },
  ),
  bulletList: makeSimpleControl('Bullet List', FormatListBulletedOutlinedIcon, {
    name: 'toggleBulletList',
  }),
  numberList: makeSimpleControl('Numbered List', FormatListNumberedOutlinedIcon, {
    name: 'toggleOrderedList',
  }),
  alignLeft: makeSimpleControl('Align Left', FormatAlignLeftOutlinedIcon, {
    name: 'setTextAlign',
    attributes: 'left',
  }),
  alignCenter: makeSimpleControl('Align Center', FormatAlignCenterOutlinedIcon, {
    name: 'setTextAlign',
    attributes: 'center',
  }),
  alignRight: makeSimpleControl('Align Right', FormatAlignRightOutlinedIcon, {
    name: 'setTextAlign',
    attributes: 'right',
  }),
  image: () => <ImageControl />,
  table: () => <TableControl />,
  latex: () => <LatexControl />,
  beta: () => <BetaControl />,
  sd: () => <SdControl />,
  summation: () => <SummationControl />,
};

function Toolbar({ controls }: ToolbarProps) {
  return (
    <div
      className='rte-toolbar flex flex-wrap gap-3 rounded-t-lg border-b border-neutral-200 bg-white px-2 py-1.5'
      role='toolbar'
    >
      {controls.map((group, i) => {
        if (Array.isArray(group)) {
          return (
            <div
              key={`group-${i}`}
              className='inline-flex divide-x divide-neutral-200 overflow-hidden rounded-md border border-neutral-300 bg-white'
              role='group'
            >
              {group.map((c) => {
                const Cmp = CONTROL_COMPONENTS[c];
                if (!Cmp) return null;
                return <Cmp key={c} />;
              })}
            </div>
          );
        }
        const Single = CONTROL_COMPONENTS[group];
        return Single ? <Single key={`single-${group}`} /> : null;
      })}
    </div>
  );
}

export default Toolbar;
